import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';
import { ProductConfigurationConfigurableOption } from '@df/module-product-configuration/model/configurable/options/product-configuration-configurable-option';
import { ProductConfigurationConfigurableOptionValue } from '@df/module-product-configuration/model/configurable/options/product-configuration-configurable-option-value';
import { ProductConfigurationConfigurable } from '@df/module-product-configuration/model/configurable/product-configuration-configurable';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { GridDirective } from '@df/ui/atomic/atom/grid/grid.directive';
import { UiButtonDirective } from '@df/ui/button/button.directive';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ProductConfigurationPlugin } from '../../../../../module/product-configuration/plugins/product-configuration-plugin';
import { ProductStatusDirective } from '../../product-status.directive';
import { ProductLayoutComponent } from '../product-layout.component';
import { ProductLayoutWishlistService, WishlistState } from '../wishlist/product-layout-wishlist.service';
import { ProductLayoutConfigurableOptionsSwatchComponent } from './swatch/product-layout-configurable-options-swatch.component';

@Component({
    selector: 'product-layout-configurable-options',
    templateUrl: './product-layout-configurable-options.component.html',
    styleUrl: './product-layout-configurable-options.component.scss',
    imports: [
        GridDirective,
        NgTemplateOutlet,
        OptionLabelPipe,
        ProductLayoutConfigurableOptionsSwatchComponent,
        ProductStatusDirective,
        TranslatePipe,
        UiButtonDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductLayoutConfigurableOptionsComponent extends UiComponent implements OnInit {
    readonly productViewLayout = input.required<ProductLayoutComponent>();
    readonly modal = input<boolean>(false);

    /**
     * Configuration service
     */
    protected readonly configuration = inject(ProductConfiguration) as ProductConfigurationConfigurable;
    protected readonly productConfigurationPlugin = inject(ProductConfigurationPlugin);
    protected readonly productLayoutWishlistService = inject(ProductLayoutWishlistService);

    /**
     * Options map
     */
    protected options?: ProductConfigurationConfigurableOption<ProductConfigurationConfigurableOptionValue>[];

    /**
     * Init hook
     */
    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.configuration.options$.subscribe(() => this.onOptionsChange()));
        this.subscriptions.add(this.configuration.selectionChange$.subscribe(() => this.onSelectionChange()));
    }

    protected select(attributeCode: string, value: number) {
        const isSelected = this.configuration.isSelected(attributeCode, value);
        const callBack = this.productLayoutWishlistService.callBack();

        if (!callBack && !isSelected) {
            if (!isSelected) {
                this.configuration.select(attributeCode, value);

                // Reset carousel position when color changes
                if (attributeCode === 'color') {
                    this.timeoutService.setTimeout(() => {
                        const productLayout = this.productViewLayout() as any;
                        productLayout.updateMedia();
                    }, 100);
                }
            } else {
                this.productViewLayout().closeProductLayoutConfigurableOptionsModal();
            }
        } else if (this.productLayoutWishlistService.state() === WishlistState.BeforeAdd && callBack) {
            if (!isSelected) {
                this.configuration.select(attributeCode, value);

                // Reset carousel position when color changes
                if (attributeCode === 'color') {
                    this.timeoutService.setTimeout(() => {
                        const productLayout = this.productViewLayout() as any;
                        productLayout.updateMedia();
                    }, 100);
                }
            }

            callBack();

            this.productLayoutWishlistService.callBack.set(undefined);

            if (isSelected) {
                this.productViewLayout().closeProductLayoutConfigurableOptionsModal();
            }
        }
    }

    protected getTooltip(error?: string): string {
        return error
            ? {
                  mismatch: 'Option is not available with current selection',
                  oos: 'Option is out of stock'
              }[error] || error
            : '';
    }

    protected hasSizeGuide(attrId: string) {
        return attrId === 'size' || attrId === 'shoe_size' || attrId === 'accessory_size';
    }

    protected getOptionSwatch(item: ProductConfigurationConfigurableOptionValue) {
        const option = item.attribute.getOptionByValue(item.value);

        if (option && typeof option !== 'string') {
            return option.swatch || '#fff';
        }

        return '#fff';
    }

    /**
     * On parent model change
     */
    private onOptionsChange() {
        this.options = this.configuration.options$.value;

        // if (this.modal() && this.productViewLayout().productConfigurableOptionsModalIsPalette) {
        //     this.options = this.options?.filter(option => option.attribute.id === 'color');
        // }

        this.options = this.options?.filter(option => option.values.length > 1);

        this.detectChanges();
    }

    /**
     * On selection change
     */
    private onSelectionChange() {
        this.detectChanges();
    }
}
